import type { ApplicationJourneyHeaderDto } from '../api/admin';
import type { ApplicationTabKey } from './applicationDetailTabStatus';
import {
  mapLoanReviewToViewModel,
  type ReviewReasonNote,
} from './loanReview/mapLoanReviewToViewModel';

export type ManualReviewAttentionItem = {
  label: string;
  detail: string;
  severity: 'warning' | 'error';
  tab?: ApplicationTabKey;
};

const FAIL_STATUSES = new Set(['FAIL', 'ERROR', 'UNAVAILABLE']);
const REVIEW_STATUSES = new Set(['REVIEW', 'PASS_WITH_VARIATION']);

function noteTab(note: ReviewReasonNote): ApplicationTabKey | undefined {
  if (note.appTab === 'employer') return 'employer';
  if (note.appTab === 'identity') return 'identity';
  if (note.appTab === 'income') return 'income';
  const text = note.text || '';
  if (/see\s+employer\s+tab|employer exists|trustworthy workplace/i.test(text)) return 'employer';
  if (/see\s+income\s+tab/i.test(text)) return 'income';
  if (/see\s+identity\s+tab/i.test(text)) return 'identity';
  return undefined;
}

function checkSeverity(status: string): 'warning' | 'error' {
  const upper = status.toUpperCase();
  return FAIL_STATUSES.has(upper) ? 'error' : 'warning';
}

/** Reviewer-facing issues from loan review — not Decision Engine capacity detail. */
export function collectManualReviewAttentionItems(
  loanReview: ApplicationJourneyHeaderDto['loanReview'] | undefined,
): ManualReviewAttentionItem[] {
  if (!loanReview?.requestStatus) return [];

  const decision = loanReview.decision?.trim().toUpperCase();
  // Agent APPROVE: soft flags and advisory notes are informational — not checks
  // requiring human review on the Manual review tab.
  if (decision === 'APPROVE') {
    return [];
  }

  const vm = mapLoanReviewToViewModel(loanReview);
  const items: ManualReviewAttentionItem[] = [];
  const seen = new Set<string>();

  const push = (item: ManualReviewAttentionItem) => {
    const key = `${item.label}|${item.detail}`;
    if (!item.detail.trim() || seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  if (vm.primaryReasonPlain?.trim()) {
    push({
      label: 'Primary reason',
      detail: vm.primaryReasonPlain.trim(),
      severity: vm.outcomeLabel === 'Not eligible' ? 'error' : 'warning',
      tab: 'loan-review',
    });
  }

  for (const note of [...vm.reviewReasonNotes, ...vm.softFlagNotes, ...vm.alsoNotedNotes]) {
    push({
      label: 'Loan review',
      detail: note.text,
      severity: 'warning',
      tab: noteTab(note) ?? 'loan-review',
    });
  }

  for (const line of vm.attention) {
    push({ label: 'Attention', detail: line, severity: 'warning', tab: 'loan-review' });
  }

  for (const card of vm.sourceCards) {
    const cardStatus = card.status.toUpperCase();
    if (cardStatus === 'PASS' || cardStatus === 'NOT_RUN') continue;

    const failedChecks = card.subChecks.filter((check) => {
      const status = check.status.toUpperCase();
      return FAIL_STATUSES.has(status) || REVIEW_STATUSES.has(status);
    });

    if (failedChecks.length) {
      for (const check of failedChecks) {
        push({
          label: card.source,
          detail: check.plain || check.label,
          severity: checkSeverity(check.status),
          tab:
            card.key === 'employer'
              ? 'employer'
              : card.key === 'identity'
                ? 'identity'
                : card.key === 'income'
                  ? 'income'
                  : card.key === 'decision_engine'
                    ? 'decision'
                    : 'loan-review',
        });
      }
      continue;
    }

    if (card.summary?.trim()) {
      push({
        label: card.source,
        detail: card.summary.trim(),
        severity: FAIL_STATUSES.has(cardStatus) ? 'error' : 'warning',
        tab:
          card.key === 'employer'
            ? 'employer'
            : card.key === 'identity'
              ? 'identity'
              : card.key === 'income'
                ? 'income'
                : card.key === 'decision_engine'
                  ? 'decision'
                  : 'loan-review',
      });
    }
  }

  return items;
}
